import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, FlaskConical, RotateCcw, Star, Beaker, Flame, Wind,
  Droplets, Sparkles, ChevronRight, Lock, Check, AlertTriangle, Eye, Info
} from 'lucide-react';

// ── Reaction Effects Config ──────────────────────────────────────────────
const EFFECTS = {
  bubbling: { label: 'Gas Released', icon: 'bubble', color: '#60a5fa' },
  fire: { label: 'Combustion', icon: 'fire', color: '#f97316' },
  precipitate: { label: 'Precipitate Formed', icon: 'precipitate', color: '#fbbf24' },
  color_change: { label: 'Color Change', icon: 'color', color: '#a78bfa' },
  fizzing: { label: 'Fizzing', icon: 'fizz', color: '#34d399' },
  heat: { label: 'Heat Released', icon: 'heat', color: '#ef4444' },
  explosion: { label: 'Violent Reaction', icon: 'explosion', color: '#f43f5e' },
};

// ── 10 Unique Missions ──────────────────────────────────────────────────
const MISSIONS = [
  {
    id: 1, title: 'Acid + Base Neutralization',
    description: 'Mix hydrochloric acid with sodium hydroxide to create table salt and water.',
    difficulty: 1,
    chemicals: [
      { id: 'hcl', name: 'HCl', fullName: 'Hydrochloric Acid', color: '#ef4444', textColor: '#fff', beakerColor: '#fca5a5' },
      { id: 'naoh', name: 'NaOH', fullName: 'Sodium Hydroxide', color: '#3b82f6', textColor: '#fff', beakerColor: '#93c5fd' },
      { id: 'h2so4', name: 'H₂SO₄', fullName: 'Sulfuric Acid', color: '#fbbf24', textColor: '#000', beakerColor: '#fde68a' },
    ],
    correctPair: ['hcl', 'naoh'],
    equation: 'HCl + NaOH → NaCl + H₂O',
    product: 'Table Salt (NaCl) + Water',
    effects: ['color_change', 'heat'],
    resultColor: '#a3e635',
    explanation: 'This is a neutralization reaction. The acid (HCl) donates H⁺ ions and the base (NaOH) donates OH⁻ ions. They combine to form water (H₂O), while Na⁺ and Cl⁻ form table salt (NaCl). The solution turns neutral (green on indicator). Heat is released — this is exothermic!',
  },
  {
    id: 2, title: 'Metal Reacts with Acid',
    description: 'Drop zinc metal into sulfuric acid and watch hydrogen gas bubble out!',
    difficulty: 1,
    chemicals: [
      { id: 'zn', name: 'Zn', fullName: 'Zinc Metal', color: '#94a3b8', textColor: '#000', beakerColor: '#cbd5e1', issolid: true },
      { id: 'h2so4', name: 'H₂SO₄', fullName: 'Sulfuric Acid', color: '#fbbf24', textColor: '#000', beakerColor: '#fde68a' },
      { id: 'nacl', name: 'NaCl', fullName: 'Salt (distractor)', color: '#e2e8f0', textColor: '#000', beakerColor: '#f1f5f9' },
    ],
    correctPair: ['zn', 'h2so4'],
    equation: 'Zn + H₂SO₄ → ZnSO₄ + H₂↑',
    product: 'Zinc Sulfate + Hydrogen Gas',
    effects: ['bubbling'],
    resultColor: '#e0e7ff',
    explanation: 'Zinc is more reactive than hydrogen in the reactivity series, so it displaces hydrogen from sulfuric acid. The zinc dissolves, forming zinc sulfate solution, while hydrogen gas (H₂) escapes as bubbles. You can test for H₂ with a burning splint — it makes a "pop" sound!',
  },
  {
    id: 3, title: 'Volcano in a Beaker',
    description: 'Mix vinegar with baking soda for an explosive fizzing reaction!',
    difficulty: 1,
    chemicals: [
      { id: 'vinegar', name: 'CH₃COOH', fullName: 'Vinegar (Acetic Acid)', color: '#fbbf24', textColor: '#000', beakerColor: '#fef3c7' },
      { id: 'baking_soda', name: 'NaHCO₃', fullName: 'Baking Soda', color: '#f0f9ff', textColor: '#000', beakerColor: '#e0f2fe', issolid: true },
      { id: 'water', name: 'H₂O', fullName: 'Water (distractor)', color: '#bfdbfe', textColor: '#000', beakerColor: '#dbeafe' },
    ],
    correctPair: ['vinegar', 'baking_soda'],
    equation: 'CH₃COOH + NaHCO₃ → CH₃COONa + H₂O + CO₂↑',
    product: 'Sodium Acetate + Water + Carbon Dioxide',
    effects: ['fizzing', 'bubbling'],
    resultColor: '#fef9c3',
    explanation: 'This is an acid-base reaction! Vinegar (acetic acid) reacts with baking soda (sodium bicarbonate) to produce CO₂ gas — that\'s the fizzing and foam you see! This is the classic "volcano" experiment. The CO₂ is the same gas we exhale and that makes soda fizzy.',
  },
  {
    id: 4, title: 'Combustion of Methane',
    description: 'Ignite methane gas with oxygen and watch it burn!',
    difficulty: 2,
    chemicals: [
      { id: 'ch4', name: 'CH₄', fullName: 'Methane Gas', color: '#a78bfa', textColor: '#fff', beakerColor: '#c4b5fd' },
      { id: 'o2', name: 'O₂', fullName: 'Oxygen Gas', color: '#60a5fa', textColor: '#fff', beakerColor: '#93c5fd' },
      { id: 'n2', name: 'N₂', fullName: 'Nitrogen (distractor)', color: '#d1d5db', textColor: '#000', beakerColor: '#e5e7eb' },
    ],
    correctPair: ['ch4', 'o2'],
    equation: 'CH₄ + 2O₂ → CO₂ + 2H₂O + Energy',
    product: 'Carbon Dioxide + Water + Heat & Light',
    effects: ['fire', 'heat'],
    resultColor: '#fef3c7',
    explanation: 'Methane burns in oxygen in a combustion reaction. This is how your gas stove works! The methane molecule breaks apart and carbon bonds with oxygen to form CO₂, while hydrogen bonds with oxygen to form water vapor. The blue flame releases a lot of energy as heat and light.',
  },
  {
    id: 5, title: 'Silver Rain',
    description: 'Mix silver nitrate with sodium chloride to see a white precipitate form!',
    difficulty: 2,
    chemicals: [
      { id: 'agno3', name: 'AgNO₃', fullName: 'Silver Nitrate', color: '#e2e8f0', textColor: '#000', beakerColor: '#f1f5f9' },
      { id: 'nacl_sol', name: 'NaCl', fullName: 'Sodium Chloride Solution', color: '#bfdbfe', textColor: '#000', beakerColor: '#dbeafe' },
      { id: 'koh', name: 'KOH', fullName: 'Potassium Hydroxide (distractor)', color: '#c4b5fd', textColor: '#000', beakerColor: '#ddd6fe' },
    ],
    correctPair: ['agno3', 'nacl_sol'],
    equation: 'AgNO₃ + NaCl → AgCl↓ + NaNO₃',
    product: 'Silver Chloride (white precipitate) + Sodium Nitrate',
    effects: ['precipitate'],
    resultColor: '#f8fafc',
    explanation: 'This is a double displacement (metathesis) reaction. Silver ions (Ag⁺) combine with chloride ions (Cl⁻) to form silver chloride (AgCl), which is insoluble in water and appears as a white, curdy precipitate that sinks to the bottom. The solution above remains clear.',
  },
  {
    id: 6, title: 'Elephant Toothpaste',
    description: 'Decompose hydrogen peroxide with a catalyst for a foamy eruption!',
    difficulty: 2,
    chemicals: [
      { id: 'h2o2', name: 'H₂O₂', fullName: 'Hydrogen Peroxide (30%)', color: '#bfdbfe', textColor: '#000', beakerColor: '#dbeafe' },
      { id: 'mno2', name: 'MnO₂', fullName: 'Manganese Dioxide (Catalyst)', color: '#1e293b', textColor: '#fff', beakerColor: '#475569', issolid: true },
      { id: 'sugar', name: 'C₁₂H₂₂O₁₁', fullName: 'Sugar (distractor)', color: '#fef3c7', textColor: '#000', beakerColor: '#fef9c3', issolid: true },
    ],
    correctPair: ['h2o2', 'mno2'],
    equation: '2H₂O₂ → (MnO₂ catalyst) → 2H₂O + O₂↑',
    product: 'Water + Oxygen Gas (rapid foam)',
    effects: ['fizzing', 'bubbling', 'heat'],
    resultColor: '#dbeafe',
    explanation: 'Hydrogen peroxide naturally decomposes into water and oxygen, but very slowly. The MnO₂ acts as a catalyst — it speeds up the reaction without being consumed! The rapid release of oxygen gas creates a massive foam eruption, like giant toothpaste squeezing out. This is exothermic!',
  },
  {
    id: 7, title: 'Rusting in Fast-Forward',
    description: 'Combine iron with oxygen and moisture to observe rust formation.',
    difficulty: 2,
    chemicals: [
      { id: 'fe', name: 'Fe', fullName: 'Iron Filings', color: '#78716c', textColor: '#fff', beakerColor: '#a8a29e', issolid: true },
      { id: 'o2_moist', name: 'O₂+H₂O', fullName: 'Moist Air (Oxygen + Water)', color: '#93c5fd', textColor: '#000', beakerColor: '#bfdbfe' },
      { id: 'co2_gas', name: 'CO₂', fullName: 'Carbon Dioxide (distractor)', color: '#d1d5db', textColor: '#000', beakerColor: '#e5e7eb' },
    ],
    correctPair: ['fe', 'o2_moist'],
    equation: '4Fe + 3O₂ + 6H₂O → 4Fe(OH)₃ → 2Fe₂O₃·3H₂O (Rust)',
    product: 'Iron Oxide (Rust) — reddish-brown',
    effects: ['color_change'],
    resultColor: '#b45309',
    explanation: 'Rusting is a slow oxidation reaction. Iron reacts with oxygen and water over time to form hydrated iron(III) oxide — the reddish-brown substance we call rust. This is why iron objects left in the rain corrode. Painting or galvanizing (coating with zinc) prevents rusting by blocking air and moisture.',
  },
  {
    id: 8, title: 'Sodium Fireworks',
    description: 'Drop sodium metal into water for a violent, fiery reaction!',
    difficulty: 3,
    chemicals: [
      { id: 'na', name: 'Na', fullName: 'Sodium Metal', color: '#f59e0b', textColor: '#000', beakerColor: '#fcd34d', issolid: true },
      { id: 'h2o_pure', name: 'H₂O', fullName: 'Pure Water', color: '#bfdbfe', textColor: '#000', beakerColor: '#dbeafe' },
      { id: 'ethanol', name: 'C₂H₅OH', fullName: 'Ethanol (distractor)', color: '#fde68a', textColor: '#000', beakerColor: '#fef9c3' },
    ],
    correctPair: ['na', 'h2o_pure'],
    equation: '2Na + 2H₂O → 2NaOH + H₂↑ + Heat',
    product: 'Sodium Hydroxide + Hydrogen Gas + Intense Heat',
    effects: ['explosion', 'fire', 'bubbling'],
    resultColor: '#fecaca',
    explanation: 'Sodium is an alkali metal — extremely reactive with water! It displaces hydrogen so violently that the heat generated ignites the H₂ gas, causing a bright yellow flame. The remaining solution is NaOH (a strong base). This is why sodium is stored under kerosene oil, never exposed to water!',
  },
  {
    id: 9, title: 'Golden Rain',
    description: 'Create a stunning yellow precipitate by mixing lead nitrate with potassium iodide!',
    difficulty: 2,
    chemicals: [
      { id: 'pbno3', name: 'Pb(NO₃)₂', fullName: 'Lead Nitrate', color: '#e2e8f0', textColor: '#000', beakerColor: '#f1f5f9' },
      { id: 'ki', name: 'KI', fullName: 'Potassium Iodide', color: '#fde68a', textColor: '#000', beakerColor: '#fef9c3' },
      { id: 'cacl2', name: 'CaCl₂', fullName: 'Calcium Chloride (distractor)', color: '#d1d5db', textColor: '#000', beakerColor: '#e5e7eb' },
    ],
    correctPair: ['pbno3', 'ki'],
    equation: 'Pb(NO₃)₂ + 2KI → PbI₂↓ + 2KNO₃',
    product: 'Lead Iodide (bright yellow precipitate) + Potassium Nitrate',
    effects: ['precipitate', 'color_change'],
    resultColor: '#facc15',
    explanation: 'This is called the "Golden Rain" experiment! When the two clear solutions mix, lead ions (Pb²⁺) combine with iodide ions (I⁻) to form lead iodide (PbI₂), a beautiful bright yellow precipitate. If you heat and slowly cool it, the crystals form golden, glittering flakes — like rain of gold!',
  },
  {
    id: 10, title: 'Copper Displacement',
    description: 'Dip an iron nail into copper sulfate solution and watch copper deposit!',
    difficulty: 3,
    chemicals: [
      { id: 'cuso4', name: 'CuSO₄', fullName: 'Copper Sulfate Solution', color: '#2563eb', textColor: '#fff', beakerColor: '#60a5fa' },
      { id: 'fe_nail', name: 'Fe', fullName: 'Iron Nail', color: '#78716c', textColor: '#fff', beakerColor: '#a8a29e', issolid: true },
      { id: 'al', name: 'Al', fullName: 'Aluminium Foil (distractor)', color: '#d1d5db', textColor: '#000', beakerColor: '#e5e7eb', issolid: true },
    ],
    correctPair: ['cuso4', 'fe_nail'],
    equation: 'CuSO₄ + Fe → FeSO₄ + Cu↓',
    product: 'Iron Sulfate (green) + Copper Metal (brown deposit)',
    effects: ['color_change'],
    resultColor: '#16a34a',
    explanation: 'Iron is more reactive than copper in the reactivity series, so it displaces copper from the solution. The blue CuSO₄ solution gradually turns green (FeSO₄) as iron dissolves, and reddish-brown copper metal deposits on the nail. This proves the activity series of metals!',
  },
];

// ── Draggable Chemical Beaker ──────────────────────────────────────────
function ChemicalBeaker({ chemical, isInMixing = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `chem-${chemical.id}`,
    data: { chemical },
  });

  return (
    <motion.div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      initial={{ scale: 0, rotate: -10 }}
      animate={{ scale: isDragging ? 1.1 : 1, rotate: 0, opacity: isDragging ? 0.4 : 1 }}
      whileHover={{ scale: 1.08, y: -4 }}
      whileTap={{ scale: 0.95 }}
      className={`flex flex-col items-center gap-1.5 cursor-grab active:cursor-grabbing select-none ${isInMixing ? 'pointer-events-none' : ''}`}
      data-testid={`chemical-${chemical.id}`}
    >
      <div className="relative">
        {/* Beaker shape */}
        <div
          className="w-16 h-20 rounded-b-2xl rounded-t-lg border-2 border-white/20 relative overflow-hidden"
          style={{ backgroundColor: chemical.beakerColor + '40' }}
        >
          {/* Liquid fill */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 rounded-b-xl"
            style={{ backgroundColor: chemical.color, height: chemical.issolid ? '30%' : '65%' }}
            animate={{ height: chemical.issolid ? ['30%', '32%', '30%'] : ['63%', '67%', '63%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* Bubbles for liquids */}
          {!chemical.issolid && (
            <>
              <motion.div className="absolute w-1.5 h-1.5 rounded-full bg-white/30"
                style={{ left: '30%' }}
                animate={{ bottom: ['20%', '80%'], opacity: [0.6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
              />
              <motion.div className="absolute w-1 h-1 rounded-full bg-muted"
                style={{ left: '60%' }}
                animate={{ bottom: ['25%', '75%'], opacity: [0.4, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, delay: 0.5 }}
              />
            </>
          )}
          {/* Solid granules */}
          {chemical.issolid && (
            <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-0.5 justify-center">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: chemical.color, opacity: 0.8 }} />
              ))}
            </div>
          )}
        </div>
        {/* Beaker rim */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-muted rounded-t-lg border-b border-border" />
      </div>
      <div className="text-center">
        <p className="text-xs font-bold" style={{ color: chemical.color }}>{chemical.name}</p>
        <p className="text-[9px] text-muted-foreground leading-tight max-w-[80px]">{chemical.fullName}</p>
      </div>
    </motion.div>
  );
}

// ── Mixing Flask (Drop Zone) ──────────────────────────────────────────
function MixingFlask({ children, isOver, hasChemicals }) {
  const { setNodeRef } = useDroppable({ id: 'mixing-flask' });

  return (
    <div ref={setNodeRef} className="relative flex flex-col items-center" data-testid="mixing-flask">
      {/* Flask neck */}
      <div className="w-8 h-6 bg-muted/50 border-x-2 border-t-2 border-white/20 rounded-t-lg" />
      {/* Flask body */}
      <motion.div
        className={`w-44 h-44 rounded-full border-2 relative overflow-hidden transition-colors ${
          isOver ? 'border-emerald-400 shadow-lg shadow-emerald-400/30' : hasChemicals ? 'border-violet-400/50' : 'border-white/20'
        }`}
        style={{ backgroundColor: isOver ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)' }}
        animate={isOver ? { scale: [1, 1.03, 1] } : {}}
        transition={{ duration: 0.5, repeat: Infinity }}
      >
        {children}
        {!hasChemicals && !isOver && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground/40">
            <Beaker className="w-8 h-8 mb-1" />
            <p className="text-xs font-medium">Drop chemicals here</p>
          </div>
        )}
        {isOver && !hasChemicals && (
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: Infinity }}>
              <Droplets className="w-10 h-10 text-emerald-400" />
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ── Reaction Animation Overlay ──────────────────────────────────────────
function ReactionAnimation({ effects, resultColor, isActive }) {
  if (!isActive) return null;

  return (
    <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
      {/* Base result color fill */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 rounded-b-full"
        initial={{ height: '0%' }}
        animate={{ height: '70%' }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
        style={{ backgroundColor: resultColor }}
      />

      {/* Bubbling effect */}
      {effects.includes('bubbling') && [...Array(12)].map((_, i) => (
        <motion.div key={`bub-${i}`}
          className="absolute rounded-full"
          style={{
            width: 4 + Math.random() * 8,
            height: 4 + Math.random() * 8,
            left: `${15 + Math.random() * 70}%`,
            backgroundColor: 'rgba(255,255,255,0.4)',
          }}
          initial={{ bottom: '20%', opacity: 0 }}
          animate={{ bottom: ['20%', '90%'], opacity: [0.7, 0], scale: [1, 1.5] }}
          transition={{ duration: 0.8 + Math.random() * 1, repeat: Infinity, delay: Math.random() * 1.5 }}
        />
      ))}

      {/* Fizzing effect */}
      {effects.includes('fizzing') && [...Array(20)].map((_, i) => (
        <motion.div key={`fizz-${i}`}
          className="absolute rounded-full bg-muted/500"
          style={{
            width: 2 + Math.random() * 5,
            height: 2 + Math.random() * 5,
            left: `${10 + Math.random() * 80}%`,
          }}
          initial={{ bottom: '30%', opacity: 0 }}
          animate={{
            bottom: ['30%', `${80 + Math.random() * 20}%`],
            opacity: [0.8, 0],
            x: [0, (Math.random() - 0.5) * 30],
          }}
          transition={{ duration: 0.4 + Math.random() * 0.6, repeat: Infinity, delay: Math.random() * 2 }}
        />
      ))}

      {/* Fire effect */}
      {effects.includes('fire') && (
        <div className="absolute bottom-[40%] left-1/2 -translate-x-1/2">
          {[...Array(8)].map((_, i) => (
            <motion.div key={`fire-${i}`}
              className="absolute rounded-full"
              style={{
                width: 10 + Math.random() * 16,
                height: 16 + Math.random() * 20,
                left: `${-20 + Math.random() * 40}px`,
                background: `radial-gradient(ellipse, ${i % 2 === 0 ? '#fbbf24' : '#f97316'}, transparent)`,
              }}
              animate={{
                y: [0, -20 - Math.random() * 30],
                opacity: [0.9, 0],
                scale: [1, 0.3],
              }}
              transition={{ duration: 0.5 + Math.random() * 0.5, repeat: Infinity, delay: Math.random() * 0.5 }}
            />
          ))}
        </div>
      )}

      {/* Precipitate effect */}
      {effects.includes('precipitate') && [...Array(15)].map((_, i) => (
        <motion.div key={`prec-${i}`}
          className="absolute rounded-sm"
          style={{
            width: 3 + Math.random() * 6,
            height: 3 + Math.random() * 6,
            left: `${15 + Math.random() * 70}%`,
            backgroundColor: resultColor,
            filter: 'brightness(1.3)',
          }}
          initial={{ top: '20%', opacity: 0 }}
          animate={{
            top: ['20%', `${65 + Math.random() * 15}%`],
            opacity: [0, 0.9],
            rotate: [0, Math.random() * 180],
          }}
          transition={{ duration: 1.5 + Math.random() * 1.5, repeat: Infinity, delay: Math.random() * 2 }}
        />
      ))}

      {/* Heat waves */}
      {effects.includes('heat') && [...Array(5)].map((_, i) => (
        <motion.div key={`heat-${i}`}
          className="absolute left-0 right-0 h-[1px]"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(239,68,68,0.3), transparent)' }}
          animate={{
            top: [`${50 + i * 8}%`, `${20 + i * 5}%`],
            opacity: [0.5, 0],
          }}
          transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3 }}
        />
      ))}

      {/* Explosion flash */}
      {effects.includes('explosion') && (
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.6), rgba(239,68,68,0.3), transparent)' }}
          animate={{ opacity: [0, 1, 0.2, 0.8, 0], scale: [0.5, 1.3, 1, 1.2, 1] }}
          transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 0.5 }}
        />
      )}

      {/* Color change swirl */}
      {effects.includes('color_change') && (
        <motion.div
          className="absolute inset-4 rounded-full"
          style={{ border: `2px solid ${resultColor}`, opacity: 0.4 }}
          animate={{ rotate: [0, 360], scale: [0.8, 1, 0.8] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        />
      )}
    </div>
  );
}

// ── Main Lab Component ──────────────────────────────────────────────────
export default function ChemReactionSimLab() {
  const navigate = useNavigate();
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission } = useLabProgress('chem-reaction', MISSIONS.length);
  const [mixingChemicals, setMixingChemicals] = useState([]);
  const [isReacting, setIsReacting] = useState(false);
  const [reactionResult, setReactionResult] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [wrongAttempt, setWrongAttempt] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [isOver, setIsOver] = useState(false);
  const [score, setScore] = useState(0);
  const [showMissions, setShowMissions] = useState(true);

  const mission = MISSIONS[currentMission];

  const resetMission = useCallback(() => {
    setMixingChemicals([]);
    setIsReacting(false);
    setReactionResult(null);
    setShowExplanation(false);
    setWrongAttempt(false);
  }, []);

  const triggerReaction = useCallback((chemicals) => {
    const ids = chemicals.map(c => c.id).sort();
    const correct = [...mission.correctPair].sort();
    const isCorrect = ids.length === 2 && ids[0] === correct[0] && ids[1] === correct[1];

    if (isCorrect) {
      setIsReacting(true);
      setReactionResult('success');
      setTimeout(() => {
        if (!completedMissions.includes(currentMission)) {
          completeMission(currentMission);
          setScore(prev => prev + mission.difficulty * 10);
        }
      }, 1500);
    } else {
      setWrongAttempt(true);
      setReactionResult('wrong');
      setTimeout(() => {
        setMixingChemicals([]);
        setWrongAttempt(false);
        setReactionResult(null);
      }, 2000);
    }
  }, [mission, currentMission, completedMissions, completeMission]);

  const handleDragStart = (event) => {
    const { active } = event;
    const chem = active.data?.current?.chemical;
    if (chem) setDragItem(chem);
  };

  const handleDragOver = (event) => {
    setIsOver(!!event.over);
  };

  const handleDragEnd = (event) => {
    setDragItem(null);
    setIsOver(false);
    const { over, active } = event;
    if (!over || over.id !== 'mixing-flask') return;
    const chem = active.data?.current?.chemical;
    if (!chem || isReacting) return;
    if (mixingChemicals.find(c => c.id === chem.id)) return;

    const newMix = [...mixingChemicals, chem];
    setMixingChemicals(newMix);
    if (newMix.length >= 2) {
      setTimeout(() => triggerReaction(newMix), 400);
    }
  };

  const selectMission = (idx) => {
    if (!unlockedMissions.includes(idx)) return;
    setCurrentMission(idx);
    setShowMissions(false);
    resetMission();
  };

  // ── Mission Selector ──
  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/labs')} className="text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-red-700 flex items-center justify-center shadow-lg shadow-rose-500/30">
                  <FlaskConical className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-base font-bold leading-tight">Chemical Reactions Simulator</h1>
                  <p className="text-[10px] text-muted-foreground">Mix, React, Discover</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-amber-400 border-amber-400/30 bg-amber-400/10">
                <Star className="w-3 h-3 mr-1 fill-amber-400" />{score} pts
              </Badge>
              <Badge variant="outline" className="text-emerald-400 border-emerald-400/30 bg-emerald-400/10">
                {completedMissions.length}/{MISSIONS.length}
              </Badge>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Choose Your Experiment</h2>
            <p className="text-muted-foreground text-sm">Each mission is a unique chemical reaction. Mix the right chemicals to discover what happens!</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const isUnlocked = unlockedMissions.includes(idx);
              const isCompleted = completedMissions.includes(idx);
              return (
                <motion.button
                  key={m.id}
                  onClick={() => selectMission(idx)}
                  disabled={!isUnlocked}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.06 }}
                  className={`relative p-5 rounded-2xl border text-left transition-all group ${
                    isCompleted
                      ? 'border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10'
                      : isUnlocked
                        ? 'border-border bg-card hover:bg-muted hover:border-border'
                        : 'border-white/5 bg-card opacity-70 cursor-not-allowed'
                  }`}
                  data-testid={`mission-${idx}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                        isCompleted ? 'bg-emerald-500 text-white' : isUnlocked ? 'bg-muted text-foreground/80' : 'bg-muted/50 text-muted-foreground/60'
                      }`}>
                        {isCompleted ? <Check className="w-4 h-4" /> : isUnlocked ? idx + 1 : <Lock className="w-3 h-3" />}
                      </span>
                      <h3 className="font-semibold text-sm">{m.title}</h3>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(m.difficulty)].map((_, i) => (
                        <Star key={i} className={`w-3 h-3 ${isCompleted ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{m.description}</p>
                  <div className="flex items-center gap-2">
                    {m.chemicals.filter(c => m.correctPair.includes(c.id)).map(c => (
                      <span key={c.id} className="text-[10px] px-2 py-0.5 rounded-full border border-border" style={{ color: c.color }}>
                        {c.name}
                      </span>
                    ))}
                    {isUnlocked && !isCompleted && <ChevronRight className="w-4 h-4 text-muted-foreground/60 ml-auto group-hover:text-muted-foreground transition-colors" />}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  // ── Lab Bench View ──
  return (
    <DndContext onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
      <div className="min-h-screen bg-background text-foreground" data-testid="chem-lab-bench">
        {/* Header */}
        <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { setShowMissions(true); resetMission(); }}
                className="text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="back-to-missions">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-sm font-bold leading-tight">Mission {currentMission + 1}: {mission.title}</h1>
                <p className="text-[10px] text-muted-foreground/70">{mission.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={resetMission} className="text-muted-foreground hover:text-foreground hover:bg-muted" data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-1" />Reset
              </Button>
              <Badge variant="outline" className="text-amber-400 border-amber-400/30 bg-amber-400/10">
                <Star className="w-3 h-3 mr-1 fill-amber-400" />{score}
              </Badge>
            </div>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
            {/* Left: Lab Bench */}
            <div className="space-y-6">
              {/* Chemical Shelf */}
              <div className="p-5 rounded-2xl border border-border bg-card" data-testid="chemical-shelf">
                <div className="flex items-center gap-2 mb-4">
                  <Beaker className="w-4 h-4 text-violet-400" />
                  <h3 className="text-sm font-semibold">Chemical Shelf</h3>
                  <span className="text-[10px] text-muted-foreground/70 ml-auto">Drag chemicals into the flask</span>
                </div>
                <div className="flex items-end gap-6 justify-center py-2">
                  {mission.chemicals.map(chem => {
                    const isInMix = mixingChemicals.find(c => c.id === chem.id);
                    return (
                      <div key={chem.id} className={isInMix ? 'opacity-30 pointer-events-none' : ''}>
                        <ChemicalBeaker chemical={chem} isInMixing={!!isInMix} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Mixing Area */}
              <div className="flex flex-col items-center py-4">
                <MixingFlask isOver={isOver} hasChemicals={mixingChemicals.length > 0}>
                  {/* Show chemicals that are in the flask */}
                  {mixingChemicals.length > 0 && !isReacting && (
                    <div className="absolute inset-0 flex items-center justify-center gap-2">
                      {mixingChemicals.map(c => (
                        <motion.div key={c.id} initial={{ scale: 0 }} animate={{ scale: 1 }}
                          className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border-2"
                          style={{ backgroundColor: c.color + '40', borderColor: c.color, color: c.textColor }}>
                          {c.name.slice(0, 4)}
                        </motion.div>
                      ))}
                      {mixingChemicals.length === 1 && (
                        <div className="w-10 h-10 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center">
                          <span className="text-[10px] text-muted-foreground/60">+?</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reaction animation */}
                  <ReactionAnimation
                    effects={mission.effects}
                    resultColor={mission.resultColor}
                    isActive={isReacting}
                  />

                  {/* Wrong attempt flash */}
                  {wrongAttempt && (
                    <motion.div
                      className="absolute inset-0 rounded-full flex items-center justify-center bg-red-500/20 border-2 border-red-500"
                      initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5 }}
                    >
                      <AlertTriangle className="w-10 h-10 text-red-400" />
                    </motion.div>
                  )}
                </MixingFlask>

                {/* Status text under flask */}
                <AnimatePresence mode="wait">
                  {reactionResult === 'success' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 text-center">
                      <p className="text-emerald-400 font-bold text-sm flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4" />Reaction Complete!
                      </p>
                    </motion.div>
                  )}
                  {reactionResult === 'wrong' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 text-center">
                      <p className="text-red-400 font-medium text-sm">No reaction! Try a different combination.</p>
                    </motion.div>
                  )}
                  {!reactionResult && mixingChemicals.length === 0 && (
                    <motion.p key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 text-xs text-muted-foreground/60">
                      Drag two chemicals from the shelf into the flask
                    </motion.p>
                  )}
                  {!reactionResult && mixingChemicals.length === 1 && (
                    <motion.p key="one" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 text-xs text-muted-foreground">
                      Now add the second chemical...
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Equation & Explanation (shown after successful reaction) */}
              <AnimatePresence>
                {isReacting && reactionResult === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    transition={{ delay: 0.8 }}
                    className="space-y-3"
                  >
                    <div className="p-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/5" data-testid="reaction-equation">
                      <p className="text-xs text-emerald-400 font-semibold mb-1">Balanced Equation</p>
                      <p className="text-lg font-mono font-bold text-foreground">{mission.equation}</p>
                      <p className="text-sm text-muted-foreground mt-1">Product: {mission.product}</p>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {mission.effects.map(eff => (
                        <span key={eff} className="text-[10px] px-2 py-1 rounded-full border flex items-center gap-1"
                          style={{ borderColor: EFFECTS[eff]?.color + '40', color: EFFECTS[eff]?.color, backgroundColor: EFFECTS[eff]?.color + '10' }}>
                          {eff === 'fire' && <Flame className="w-3 h-3" />}
                          {eff === 'bubbling' && <Wind className="w-3 h-3" />}
                          {eff === 'fizzing' && <Droplets className="w-3 h-3" />}
                          {eff === 'heat' && <Flame className="w-3 h-3" />}
                          {eff === 'precipitate' && <Droplets className="w-3 h-3" />}
                          {eff === 'color_change' && <Sparkles className="w-3 h-3" />}
                          {eff === 'explosion' && <Sparkles className="w-3 h-3" />}
                          {EFFECTS[eff]?.label}
                        </span>
                      ))}
                    </div>

                    <Button variant="ghost" size="sm" onClick={() => setShowExplanation(!showExplanation)}
                      className="text-muted-foreground hover:text-foreground hover:bg-muted" data-testid="toggle-explanation">
                      <Eye className="w-4 h-4 mr-1.5" />{showExplanation ? 'Hide' : 'Show'} Explanation
                    </Button>

                    {showExplanation && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                        className="p-4 rounded-xl border border-blue-500/20 bg-blue-500/5" data-testid="explanation-box">
                        <p className="text-xs text-blue-300 font-semibold mb-1.5 flex items-center gap-1">
                          <Info className="w-3 h-3" />Why does this happen?
                        </p>
                        <p className="text-sm text-foreground/80 leading-relaxed">{mission.explanation}</p>
                      </motion.div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <Button onClick={resetMission} variant="outline"
                        className="border-white/20 text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="try-again-btn">
                        <RotateCcw className="w-4 h-4 mr-1.5" />Try Again
                      </Button>
                      {currentMission < MISSIONS.length - 1 && (
                        <Button onClick={() => { setCurrentMission(prev => prev + 1); resetMission(); }}
                          className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500" data-testid="next-mission-btn">
                          Next Experiment <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                      {currentMission === MISSIONS.length - 1 && completedMissions.length === MISSIONS.length && (
                        <Button onClick={() => setShowMissions(true)}
                          className="bg-gradient-to-r from-amber-500 to-orange-600">
                          <Star className="w-4 h-4 mr-1.5 fill-white" />All Missions Complete!
                        </Button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Right: Mission Info Panel */}
            <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
              <div className="p-4 rounded-2xl border border-border bg-card">
                <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                  <FlaskConical className="w-4 h-4 text-rose-400" />Mission Objective
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{mission.description}</p>
                <div className="mt-3 flex items-center gap-1">
                  <span className="text-[10px] text-muted-foreground/70">Difficulty:</span>
                  {[...Array(3)].map((_, i) => (
                    <Star key={i} className={`w-3 h-3 ${i < mission.difficulty ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/20'}`} />
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-2xl border border-border bg-card">
                <h3 className="font-semibold text-sm mb-2">How to Play</h3>
                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                  <li>Examine the chemicals on the shelf</li>
                  <li>Drag one chemical into the mixing flask</li>
                  <li>Drag a second chemical to start the reaction</li>
                  <li>Watch the reaction and read the explanation!</li>
                </ol>
              </div>

              <div className="p-4 rounded-2xl border border-border bg-card">
                <h3 className="font-semibold text-sm mb-2">Progress</h3>
                <div className="space-y-1.5">
                  {MISSIONS.map((m, idx) => (
                    <button key={m.id} onClick={() => { if (unlockedMissions.includes(idx)) { setCurrentMission(idx); resetMission(); } }}
                      disabled={!unlockedMissions.includes(idx)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                        idx === currentMission ? 'bg-primary text-primary-foreground' :
                        completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' :
                        unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                      }`}
                      data-testid={`sidebar-mission-${idx}`}>
                      <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${
                        completedMissions.includes(idx) ? 'bg-emerald-500 text-white' :
                        idx === currentMission ? 'bg-primary text-primary-foreground' :
                        unlockedMissions.includes(idx) ? 'bg-muted/50' : 'bg-muted/50'
                      }`}>
                        {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx + 1 : <Lock className="w-2.5 h-2.5" />}
                      </span>
                      <span className="truncate">{m.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {dragItem && (
          <div className="flex flex-col items-center gap-1 opacity-90">
            <div className="w-16 h-20 rounded-b-2xl rounded-t-lg border-2 border-white/30 relative overflow-hidden shadow-2xl"
              style={{ backgroundColor: dragItem.beakerColor + '60' }}>
              <div className="absolute bottom-0 left-0 right-0 rounded-b-xl"
                style={{ backgroundColor: dragItem.color, height: dragItem.issolid ? '30%' : '65%' }} />
            </div>
            <p className="text-xs font-bold" style={{ color: dragItem.color }}>{dragItem.name}</p>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
